/**
 * Biometric layouts: how the server reads a device export.
 * Column refs are stored as letters ("B").
 */
export const LAYOUTS = {
  PUNCH_ROWS: {
    label: "One punch per row",
    help: "Each row is one punch: employee, date/time, maybe IN/OUT.",
  },
  DAILY_ROWS: {
    label: "One day per row",
    help: "Each row is an employee's day with several time columns.",
  },
  EMPLOYEE_BLOCKS: {
    label: "Block per employee",
    help: "A header line with the employee, then one row per day (typical DTR printout).",
  },
};
export const EMPLOYEE_MATCH = {
  EMPLOYEE_NUMBER: "Employee number",
  BIOMETRIC_ID: "Biometric ID (linked per device system)",
  NAME: "Employee name",
};
export const DATE_ORDERS = {
  MDY: "Month/Day/Year",
  DMY: "Day/Month/Year",
  YMD: "Year-Month-Day",
};
export const DEFAULT_EMPLOYEE_PATTERN = "^(?<name>.+?)\\s*\\((?<id>[^)]+)\\)$";

/** Column roles per layout; `multi` roles take several columns in order. */
export const ROLES = {
  PUNCH_ROWS: [
    { key: "employee", label: "Employee ID" },
    { key: "employeeName", label: "Employee name" },
    { key: "dateTime", label: "Date + time" },
    { key: "date", label: "Date" },
    { key: "time", label: "Time" },
    { key: "type", label: "IN / OUT" },
    { key: "device", label: "Device" },
  ],
  DAILY_ROWS: [
    { key: "employee", label: "Employee ID" },
    { key: "employeeName", label: "Employee name" },
    { key: "date", label: "Date" },
    { key: "punches", label: "Time columns", multi: true },
  ],
  EMPLOYEE_BLOCKS: [
    { key: "employeeColumn", label: "Employee header", top: true },
    { key: "dateColumn", label: "Date", top: true },
    { key: "punchColumns", label: "Time columns", multi: true, top: true },
  ],
};

export const ROLE_COLORS = {
  employee: "#2563eb",
  employeeName: "#7c3aed",
  employeeColumn: "#2563eb",
  dateTime: "#0d9488",
  date: "#0d9488",
  dateColumn: "#0d9488",
  time: "#ca8a04",
  punches: "#ca8a04",
  punchColumns: "#ca8a04",
  type: "#db2777",
  device: "#64748b",
};

export const emptyConfig = (layout = "PUNCH_ROWS") => ({
  layout,
  employeeMatch: "EMPLOYEE_NUMBER",
  dateOrder: "MDY",
  overnight: true,
  headerRow: 1,
  columns: {},
  typeValues: { in: ["IN", "I", "0"], out: ["OUT", "O", "1"] },
  punchTypes: "ALTERNATE",
  employeePattern: "",
  employeeColumn: "",
  dateColumn: "",
  punchColumns: [],
});

/** Spreadsheet column letter for a 0-based index (0 → A, 26 → AA). */
export const columnLetter = (index) => {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/** Which roles a column has in this config (for the grid highlights). */
export const rolesOfColumn = (config, letter) => {
  const out = [];
  if (config.layout === "EMPLOYEE_BLOCKS") {
    if (config.employeeColumn === letter) out.push("employeeColumn");
    if (config.dateColumn === letter) out.push("dateColumn");
    const i = (config.punchColumns || []).indexOf(letter);
    if (i >= 0) out.push(`punchColumns:${i}`);
    return out;
  }
  Object.entries(config.columns || {}).forEach(([role, v]) => {
    if (Array.isArray(v)) {
      const i = v.indexOf(letter);
      if (i >= 0) out.push(`${role}:${i}`);
    } else if (v === letter) out.push(role);
  });
  return out;
};

/** Toggle a column for a role (single roles move, multi roles add/remove). */
export const assignColumn = (config, role, letter) => {
  const def = (ROLES[config.layout] || []).find((r) => r.key === role);
  if (!def) return config;
  if (def.top) {
    if (def.multi) {
      const list = config[role] || [];
      const next = list.includes(letter)
        ? list.filter((x) => x !== letter)
        : [...list, letter];
      return {
        ...config,
        [role]: next,
        punchTypes: Array.isArray(config.punchTypes)
          ? fitPunchTypes(config.punchTypes, next.length)
          : config.punchTypes,
      };
    }
    return { ...config, [role]: config[role] === letter ? "" : letter };
  }
  const columns = { ...(config.columns || {}) };
  if (def.multi) {
    const list = columns[role] || [];
    columns[role] = list.includes(letter)
      ? list.filter((x) => x !== letter)
      : [...list, letter];
    return {
      ...config,
      columns,
      punchTypes: Array.isArray(config.punchTypes)
        ? fitPunchTypes(config.punchTypes, columns[role].length)
        : config.punchTypes,
    };
  }
  if (columns[role] === letter) delete columns[role];
  else columns[role] = letter;
  return { ...config, columns };
};

const fitPunchTypes = (types, length) =>
  Array.from({ length }, (_, i) => (types[i] === undefined ? null : types[i]));

/** Time columns for the layout (DAILY_ROWS → columns.punches, BLOCKS → punchColumns). */
export const punchColumnsOf = (config) =>
  config.layout === "EMPLOYEE_BLOCKS"
    ? config.punchColumns || []
    : config.layout === "DAILY_ROWS"
      ? config.columns?.punches || []
      : [];

/** What's still missing before the layout can be saved (client-side hints; the server validates). */
export const configProblems = (config) => {
  const p = [];
  const c = config.columns || {};
  if (config.layout === "PUNCH_ROWS") {
    if (!c.employee && !c.employeeName) p.push("Pick the employee column.");
    if (!c.dateTime && !(c.date && c.time))
      p.push("Pick a date + time column, or a date and a time column.");
  } else if (config.layout === "DAILY_ROWS") {
    if (!c.employee && !c.employeeName) p.push("Pick the employee column.");
    if (!c.date) p.push("Pick the date column.");
    if (!(c.punches || []).length) p.push("Pick at least one time column.");
  } else if (config.layout === "EMPLOYEE_BLOCKS") {
    if (!config.employeeColumn)
      p.push("Pick the column with the employee header.");
    if (!config.dateColumn) p.push("Pick the date column.");
    if (!(config.punchColumns || []).length)
      p.push("Pick at least one time column.");
  }
  if (
    config.employeeMatch === "NAME" &&
    config.layout !== "EMPLOYEE_BLOCKS" &&
    !c.employeeName &&
    !c.employee
  )
    p.push("Pick the employee name column.");
  return p;
};

const list = (v) => (v || []).filter((x) => x !== "" && x !== undefined);

/** The config as sent to the server: only what the layout uses. */
export const cleanConfig = (config) => {
  const base = {
    layout: config.layout,
    employeeMatch: config.employeeMatch,
    dateOrder: config.dateOrder,
    overnight: config.overnight !== false,
    ...(config.sheet ? { sheet: config.sheet } : {}),
  };
  if (config.layout === "EMPLOYEE_BLOCKS") {
    return {
      ...base,
      employeeColumn: config.employeeColumn,
      dateColumn: config.dateColumn,
      punchColumns: list(config.punchColumns),
      punchTypes: config.punchTypes,
      ...(config.employeePattern?.trim()
        ? { employeePattern: config.employeePattern.trim() }
        : {}),
    };
  }
  const columns = Object.fromEntries(
    Object.entries(config.columns || {}).filter(([, v]) =>
      Array.isArray(v) ? v.length : v,
    ),
  );
  return {
    ...base,
    headerRow:
      Number.isFinite(Number(config.headerRow)) && config.headerRow !== ""
        ? Number(config.headerRow)
        : 1, // 0 = no header
    columns,
    ...(config.layout === "PUNCH_ROWS" && columns.type
      ? {
          typeValues: {
            in: list(config.typeValues?.in),
            out: list(config.typeValues?.out),
          },
        }
      : {}),
    ...(config.layout === "DAILY_ROWS"
      ? { punchTypes: config.punchTypes }
      : {}),
  };
};

/** A server/preset config → editor state (fills the fields the editor needs). */
export const toEditable = (config) => {
  const base = emptyConfig(config?.layout || "PUNCH_ROWS");
  const c = { ...base, ...(config || {}) };
  return {
    ...c,
    columns: { ...(config?.columns || {}) },
    typeValues: {
      in: config?.typeValues?.in || base.typeValues.in,
      out: config?.typeValues?.out || base.typeValues.out,
    },
    punchColumns: config?.punchColumns || [],
    employeePattern: config?.employeePattern || "",
  };
};

/** "IN, I, 0" ↔ ["IN","I","0"] */
export const splitValues = (text) =>
  text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/* ---------- Which layout an upload used ---------- */
// Layout <select> values: "" = automatic, "t:<id>" = saved, "p:<key>" = preset
const CHOSEN_BY = {
  PICKED: "Chosen by you",
  PRESET: "Preset",
  BRANCH: "From branch",
  DETECTED: "Detected",
  DEFAULT: "Default",
};

/** "Detected", "From branch"…; older responses and unsaved tests: "Read with". */
export const chosenByLabel = (chosenBy) => CHOSEN_BY[chosenBy] || "Read with";

/**
 * The <select> value of a layout named by a dry run. Presets have no id, so
 * they are matched by name. "" when it can't be told (never guess one).
 */
export const sourceOfLayout = ({ id, name } = {}, presets = []) => {
  if (id) return `t:${id}`;
  const preset = presets.find((p) => p.name === name);
  return preset ? `p:${preset.key}` : "";
};

/** Exactly the layout the dry run used, for the real import; null if unknown. */
export const importParamsOf = (result, presets = []) => {
  const source = sourceOfLayout(result?.template, presets);
  if (source.startsWith("t:")) return { templateId: source.slice(2) };
  if (source.startsWith("p:")) return { preset: source.slice(2) };
  return null;
};

/** "Device log: 120 punches, 2 problems" */
export const candidateLabel = ({ name, punches = 0, issues = 0 }) =>
  `${name}: ${punches} punch${punches === 1 ? "" : "es"}${
    issues ? `, ${issues} problem${issues === 1 ? "" : "s"}` : ""
  }`;

/* ---------- Branches of a layout ---------- */
/** Branches to offer: the layout's company only (null = any), plus what is already chosen. */
export const branchChoices = (branches, companyId, selected = []) =>
  branches.filter(
    (b) =>
      !companyId ||
      (b.companyId || b.company?.id) === companyId ||
      selected.includes(b.id),
  );

/** Chosen branches another layout holds now: saving moves them here. */
export const movedBranches = (selected, layouts, layoutId, branches = []) =>
  selected.flatMap((id) => {
    const from = layouts.find(
      (l) => l.id !== layoutId && (l.branchIds || []).includes(id),
    );
    if (!from) return [];
    const branch = branches.find((b) => b.id === id);
    return [
      { id, branchName: branch?.branchName || "A branch", from: from.name },
    ];
  });

/** Branch names of a layout, for the list. */
export const branchNamesOf = (layout, branches = []) =>
  (layout.branchIds || []).map(
    (id) => branches.find((b) => b.id === id)?.branchName || "Unknown branch",
  );
